<script lang="ts">
  // Svelte
  import { browser } from '$app/environment';
  import { onMount, type ComponentEvents } from 'svelte';
  import { page } from '$app/stores';
  import { goto } from '$app/navigation';
  // Stores
  import { me } from '$src/stores/user.store';
  import { homeSelectedTab, isHomeMenuOpen } from '$stores/nav.store';
  import { myProfileSelectedTab } from '$stores/nav.store';
  import { postInModal, searchOpen } from '$stores';
  // HomePage Components
  import TopIsland from '$lib/components/homepage/TopIsland.svelte';
  import FilterModal from '$lib/components/homepage/FilterModal.svelte';
  import FocusedSearchbar from '$lib/components/homepage/FocusedSearchbar/FocusedSearchbar.svelte';
  // Homepage Tabs
  import HomeForYouTab from '$lib/components/homepage/tabs/HomeForYouTab.svelte';
  import DynamicExploreTab from '$lib/components/homepage/tabs/DynamicExploreTab.svelte';
  import GalleriesTab from '$lib/components/homepage/tabs/GalleriesTab.svelte';
  import ChallengesTab from '$lib/components/homepage/tabs/ChallengesTab.svelte';
  import { hometabs } from '$lib/components/homepage/tabs/hometabs';
  // Modals
  import PeopleFilterModal from '$lib/components/homepage/PeopleFilterModal.svelte';
  import GalleryFilterModal from '$lib/components/homepage/GalleryFilterModal.svelte';
  import BetaWelcomeModal from '$lib/components/Modals/BetaWelcomeModal.svelte';
  // Actions
  import {
    useScrollVersion,
    useScrollVersionController
  } from '$lib/components/homepage/top-island';
  // Common Components
  import FullscreenMediaDisplay from '$lib/components/common/FullscreenMediaDisplay/FullscreenMediaDisplay.svelte';
  import FullscreenOverlay from '$lib/components/common/FullscreenOverlay/FullscreenOverlay.svelte';
  import HamburgerMenu from '$lib/components/nav/HamburgerMenu.svelte';
  import Help from '$pages/Help/index.svelte';
  // Types
  import type { SearchType } from 'garden-common';
  import type { DropdownItem } from '$lib/components/common/dropdowns/dropdown';
  import type { DynamicSearchParams } from '$src/types/homepage.interface';
  import type {
    GalleryFiltersSchema,
    PeopleFiltersSchemaWithDynamic,
    PostFiltersSchemaWithDynamic,
    SortOption
  } from '$src/utils/api/feed.util';

  let tabParam = $page.params.tab || 'for-you';

  myProfileSelectedTab.set(' ');

  if ($homeSelectedTab == undefined) {
    homeSelectedTab.set('home');
  }

  $: showBetaWelcomeModal = false;

  const checkWelcomePopup = () => {
    const userAccountAge = (Date.now() - new Date($me.createdAt).getTime()) / (1000 * 60);
    if (userAccountAge > 30) return;
    if (localStorage.getItem('welcomePopup') === null) {
      localStorage.setItem('welcomePopup', 'true');
      return true;
    }
    return localStorage.getItem('welcomePopup') === 'true';
  };

  let selectedTab: (typeof hometabs)[number] =
    hometabs.find(i => i.value === tabParam) || hometabs[0];

  let searchParams: DynamicSearchParams = {} as any;

  $: if (selectedTab.value !== tabParam) {
    if (browser) {
      tabParam = selectedTab.value;
      goto('/' + selectedTab.value);
    }
  }

  // Sort
  let selectedSortOption: DropdownItem<SortOption>;
  let selectedPeopleSortOption: DropdownItem<SortOption>;

  function onApplySort(ev: ComponentEvents<TopIsland>['sortChanged']) {
    selectedSortOption = ev.detail.sort;
  }

  function onApplyPeopleSort(ev: ComponentEvents<TopIsland>['sortChanged']) {
    selectedPeopleSortOption = ev.detail.sort;
  }

  // Filters
  let filterModalType: null | DynamicSearchParams['type'] = null;

  let filters: PostFiltersSchemaWithDynamic = { dynamicFilters: {} };
  let peopleFilters: PeopleFiltersSchemaWithDynamic = { dynamicFilters: {} };
  let galleryFilters: GalleryFiltersSchema = {};

  function onApplyFilters(ev: ComponentEvents<FilterModal>['applyFilters']) {
    filterModalType = null;

    filters = ev.detail.filters;
  }

  function onApplyPeopleFilters(ev: ComponentEvents<PeopleFilterModal>['applyPeopleFilters']) {
    filterModalType = null;
    peopleFilters = ev.detail.filters;
  }

  function onApplyGalleryFilters(ev: ComponentEvents<GalleryFilterModal>['applyGalleryFilters']) {
    filterModalType = null;
    galleryFilters = ev.detail.filters;
  }

  let fullscreenImageUrl: string | null = null;

  function closePostFullscreen() {
    fullscreenImageUrl = null;
  }

  let selectedMediaIndex: number;
  let groupIndex: number;

  let searchbarHasFocus = false;

  function onShowAllResults(ev: ComponentEvents<FocusedSearchbar>['showAllResults']) {
    searchTerm = ev.detail.searchTerm;
    searchParams.type = 'searchTerm';

    selectedTab = hometabs.find(i => i.value === 'explore');
    searchbarHasFocus = false;
  }

  let searchTerm: string = '';
  let searchType: SearchType = 'all';

  function onTabClick() {
    searchTerm = '';
    searchParams.type = 'posts';
    $useScrollVersion = false;
  }

  let hamburgerOpen = false;
  let showHelpModal = false;

  onMount(async () => {
    if (!$me) {
      $isHomeMenuOpen = true;
      return;
    }
    showBetaWelcomeModal = checkWelcomePopup();
  });
</script>

{#if showBetaWelcomeModal}
  <BetaWelcomeModal
    on:ModalClose={() => {
      showBetaWelcomeModal = false;
      localStorage.setItem('welcomePopup', 'false');
    }}
  />
{/if}

{#if filterModalType === 'posts'}
  <FilterModal
    initialFilters={filters}
    {filters}
    on:applyFilters={onApplyFilters}
    on:clickModalClose={() => (filterModalType = null)}
  />
{/if}

{#if filterModalType === 'users'}
  <PeopleFilterModal
    initialFilters={peopleFilters}
    filters={peopleFilters}
    on:applyPeopleFilters={onApplyPeopleFilters}
    on:clickModalClose={() => (filterModalType = null)}
  />
{/if}

{#if filterModalType === 'galleries'}
  <GalleryFilterModal
    initialFilters={galleryFilters}
    filters={galleryFilters}
    on:applyGalleryFilters={onApplyGalleryFilters}
    on:clickModalClose={() => (filterModalType = null)}
  />
{/if}

{#if fullscreenImageUrl !== null}
  <FullscreenMediaDisplay
    {groupIndex}
    mediaIndex={selectedMediaIndex}
    media={$postInModal.post.media}
    options={{ imageUrl: fullscreenImageUrl, compressImageUrl: null }}
    on:clickClose={closePostFullscreen}
  />
{/if}

{#if searchbarHasFocus || $searchOpen}
  <FullscreenOverlay>
    <FocusedSearchbar
      on:close={() => (searchbarHasFocus = $searchOpen = false)}
      on:showAllResults={onShowAllResults}
      bind:searchType
      {searchTerm}
    />
  </FullscreenOverlay>
{/if}

<div class="fixed w-full h-[10rem] -z-10 bg-gradient-to-b from-garden-grey-300 to-[#d0ddd700]" />

<div
  class="flex relative overflow-x-clip z-0 flex-grow flex-col lg:flex-row max-w-screen lg:px-4 lg:ml-10 lg:bg-transparent"
  class:lg:ml-20={$page.url.toString().includes('for-you')}
>
  {#if showHelpModal}
    <Help on:close={() => (showHelpModal = false)} />
  {/if}

  {#if hamburgerOpen}
    <HamburgerMenu
      on:showHelpModal={() => (showHelpModal = true)}
      on:ModalClose={() => (hamburgerOpen = false)}
    />
  {/if}

  <div
    class=" mx-auto w-full max-w-screen-lg h-fit flex flex-col overflow-scroll transition-all duration-200 scrollbar-hide {$useScrollVersion
      ? 'mt-10'
      : 'mt-24'}"
    class:lg:pt-4={!$useScrollVersion}
  >
    <!-- This needs to be stuck to the top  -->
    <TopIsland
      bind:selectedTab
      bind:searchParams
      bind:searchTerm
      on:clickEditFilters={e => (filterModalType = e.detail.searchTab)}
      on:sortChanged={onApplySort}
      on:peopleSortChanged={onApplyPeopleSort}
      on:searchbarFocus={() => (searchbarHasFocus = true)}
      on:tabClick={onTabClick}
      on:clickedHamburger={() => (hamburgerOpen = !hamburgerOpen)}
    />

    <!-- these tabs need to make their content stretch the parent  -->
    <div class="mt-12 min-w-screen-lg mx-auto w-full">
      {#if selectedTab.value === 'for-you'}
        <HomeForYouTab {filters} sort={selectedSortOption?.customData || 'new-to-old'} />
      {:else if selectedTab.value === 'explore'}
        <DynamicExploreTab
          mode={searchParams.type}
          sort={selectedSortOption?.customData}
          peopleSort={selectedPeopleSortOption?.customData}
          {filters}
          {peopleFilters}
          {searchTerm}
          {searchType}
        >
          <div slot="title" class="w-full pb-5 overflow-auto">
            <div class="w-full font-black flex flex-row justify-between uppercase">
              <span class="uppercase text-xl text-garden-grey-700 font-black">Explore Posts</span>
            </div>
          </div>
        </DynamicExploreTab>
      {:else if selectedTab.value === 'galleries'}
        <GalleriesTab
          filters={galleryFilters}
          sort={selectedSortOption?.customData || 'new-to-old'}
        />
      {:else if selectedTab.value === 'challenges'}
        <ChallengesTab
          filters={galleryFilters}
          sort={selectedSortOption?.customData || 'new-to-old'}
        />
      {/if}
    </div>
  </div>
</div>
